import { MethodType, ParamsFetchType, GenerateUrlApiType, ResponseType } from 'src_common/browser/apiUtils';
import { GenerateUrlApiParamsType } from 'src_common/server/webDriver/sdkApiWebUtils';
import qs from 'query-string';
import { fetchGeneralRaw, FetchGeneralRawResponseType } from 'src_common/common/fetch';
import { ApiTimeLog } from 'src_common/server/webDriver/logFormat';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';

const CustomerItemIO = t.interface({
    id: t.number,
    firstName: t.union([t.null, t.undefined, t.string]),
    surname: t.union([t.null, t.undefined, t.string]),
    birthDate: t.string,
    postCode: t.union([t.null, t.undefined, t.string]),
    status: t.string,
    kycStatus: t.string,
});

export type CustomerItemType = t.TypeOf<typeof CustomerItemIO>;

export const isValid = createGuard(t.array(CustomerItemIO));

const decode = (status: number, data: ResponseType): Array<CustomerItemType> => {
    if (data.type === 'json') {
        if (isValid(data.json)) {
            return data.json;
        }
    }

    throw new Error(`unhandled response ${status} - ${data.type}`);
};

interface ParamsType {
    data: Record<string, unknown>;
}

export const getCustomers = {
    browser: {
        params: (params: ParamsType): ParamsFetchType<ParamsType> => {
            return {
                type: MethodType.POST,
                url: '/api-web/customers',
                body: {
                    data: params.data,
                },
            };
        },
        decode: decode,
    },
    express: {
        method: MethodType.POST,
        urlBrowser: '/api-web/customers',
    },
    generateUrlApi: async (params: GenerateUrlApiParamsType<ParamsType>): Promise<GenerateUrlApiType> => {
        if (params.userSessionId === null) {
            return {
                passToBackend: false,
                status: 403,
                responseBody: {
                    errorMessage: 'User id invalid',
                },
            };
        }

        const query = qs.stringify(params.req.body.data);

        const response = await getCustomersRequest(params.API_URL, params.API_TIMEOUT, params.jwtToken, {
            universe: params.API_UNIVERSE,
            body: query,
        });

        return {
            passToBackend: false,
            status: response.status,
            responseBody: response.body,
        };
    },
};

interface ParamsRequestType {
    universe: string;
    body: string;
}

const getCustomersRequest = async (
    api_url: string,
    api_timeout: number,
    backendToken: string,
    params: ParamsRequestType
): Promise<FetchGeneralRawResponseType> => {
    const url = `${api_url}/accounts/${params.universe}/customer?${params.body}`;
    const method = 'GET';
    const paramsFetch = {
        url,
        backendToken,
        timeout: api_timeout,
    };

    const apiTime = ApiTimeLog.createWithProcessEnv(method, url);
    const response = await fetchGeneralRaw(method, paramsFetch);
    apiTime.show(response.status);
    return response;
};
